import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ThirdPartySupplierSecurity_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Third-Party & Supplier Security Risk Management
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Structured process to identify, assess, mitigate, and monitor
            security risks from vendors, suppliers, contractors, and third-party
            service providers.
          </p>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Third-Party & Supplier Security Risk Management Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              Third-Party & Supplier Security Risk Management is a critical
              process that ensures that the organizations you rely on for goods
              and services—your suppliers and third parties—are adequately
              securing their systems, data, and operations. In today’s highly
              interconnected world, a single security breach in a supplier's
              system or in a third-party service can have cascading effects,
              exposing your organization to data loss, financial penalties,
              reputational damage, and legal liabilities.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Effective third-party and supplier security risk management helps
              organizations identify, assess, and mitigate security risks that
              arise from their relationships with third-party vendors,
              suppliers, contractors, and service providers. This process
              ensures that all external parties align with your organization’s
              security standards, protecting your assets and data and
              maintaining the integrity of business operations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is Third-Party & Supplier Security Risk Management?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              Third-Party & Supplier Security Risk Management involves the
              identification, assessment, and management of security risks that
              arise from external parties involved in providing goods, services,
              or technology to your organization. These third parties may
              include vendors, contractors, service providers, consultants, and
              other external entities that have access to your organization’s
              critical systems, data, or infrastructure.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              Key components of third-party & supplier risk management include:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Identifying risks: Understanding potential risks that external suppliers and partners pose to your organization’s assets, data, and operational integrity.",
                "Assessing security controls: Evaluating the security posture of your suppliers and third parties to ensure they comply with industry standards and regulations.",
                "Mitigating risks: Implementing controls to minimize the likelihood and impact of security incidents caused by third-party relationships.",
                "Monitoring and auditing: Continuously monitoring and auditing third-party suppliers to ensure they remain compliant with security policies and contractual agreements.",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-gray-600 text-lg leading-relaxed mt-6">
              ISO standards like ISO 27001 and ISO 22301 also address
              third-party risk management in terms of data protection, business
              continuity, and information security, ensuring organizations are
              prepared to handle risks that arise from their external
              relationships.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of Third-Party & Supplier Security Risk Management
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="space-y-8">
            {[
              {
                number: "1",
                title: "Risk Identification and Mapping",
                desc: "The first step in third-party security risk management is to identify and map all third-party relationships and understand the potential risks they introduce. This includes access to sensitive data (e.g., customer information, intellectual property), integration with critical systems (e.g., cloud services, supply chain management), and data transfer and communication channels that may expose vulnerabilities.",
              },
              {
                number: "2",
                title: "Due Diligence and Risk Assessment",
                desc: "Perform thorough due diligence to assess the security practices, compliance, and resilience of third-party suppliers. This process should include evaluating security policies and controls, assessing the third party's cybersecurity posture (including compliance with standards like ISO 27001, SOC 2, or GDPR), and understanding the supplier’s incident response capabilities, disaster recovery plans, and business continuity measures.",
              },
              {
                number: "3",
                title: "Contractual Security Requirements",
                desc: "Contractual agreements with third-party suppliers should include specific security requirements that align with your organization’s risk management policies. This should address data protection and confidentiality agreements, third-party access controls and audit rights, security incident notification and reporting protocols, and compliance obligations (e.g., GDPR, HIPAA, or industry-specific regulations). Service Level Agreements (SLAs) should clearly define security expectations and penalties for non-compliance.",
              },
              {
                number: "4",
                title: "Continuous Monitoring and Auditing",
                desc: "Ongoing monitoring of third-party and supplier activities is critical to ensure that they continue to adhere to security standards and contractual obligations. Regular audits, security assessments, and compliance checks should be performed to evaluate the effectiveness of security measures, ensure compliance with contractual agreements and regulatory requirements, and identify any emerging security risks associated with changes in the third party’s operations or environment. Monitoring should extend to both cybersecurity and physical security.",
              },
              {
                number: "5",
                title: "Incident Response and Recovery",
                desc: "Develop and implement clear incident response protocols with third-party suppliers to ensure prompt and coordinated action in case of a security breach or data leak. This should include incident notification timelines, roles and responsibilities in the event of a security incident, and a recovery plan to mitigate the impact and recover business operations.",
              },
              {
                number: "6",
                title: "Third-Party Risk Reporting",
                desc: "Regular risk reporting should be established to ensure visibility of any third-party risks within the organization. Senior management and relevant stakeholders should be kept informed about risks identified in third-party operations, security incidents involving third parties, and risk mitigation actions and the effectiveness of those controls. These reports should inform risk decisions and help prioritize actions to address high-impact vulnerabilities.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex items-start gap-6 bg-gray-50 p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-shadow"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-xl">
                  {item.number}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why Third-Party & Supplier Security Risk Management Matters for
              Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Mitigates Cybersecurity Risks — Third-party vendors and suppliers often present significant cybersecurity risks. By actively managing these risks, organizations can reduce the likelihood of data breaches, malware infections, and other cyber threats originating from third-party relationships.",
                "Ensures Regulatory Compliance — Effective third-party risk management ensures compliance with regulatory requirements such as GDPR, HIPAA, and PCI DSS. It ensures that third-party suppliers meet data protection standards, preventing fines and legal penalties.",
                "Protects Business Continuity — Supplier and third-party disruptions can significantly affect your operations. By assessing and managing third-party risks, organizations can maintain business continuity, reduce service disruptions, and enhance resilience in case of incidents.",
                "Strengthens Vendor Relationships — Risk management fosters stronger relationships with suppliers by setting clear expectations and ensuring that they meet your organization’s security standards. It helps build trust and promotes collaboration for mutual benefit.",
                "Prevents Financial Loss — Third-party incidents, such as data breaches or service outages, can lead to substantial financial losses. By proactively managing supplier risks, businesses can avoid significant financial damage and protect their bottom line.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does Third-Party & Supplier Security Risk Management Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              The Third-Party & Supplier Security Risk Management process
              includes:
            </p>
            <div className="space-y-4">
              {[
                "1. Third-Party Identification and Mapping — Identify and map all third-party relationships that impact your data, assets, or operations, including vendors, contractors, consultants, service providers, and partners.",
                "2. Risk Assessment and Due Diligence — Conduct a comprehensive risk assessment to evaluate the security posture of each third-party supplier and their potential risk to the organization. Perform due diligence to assess their ability to manage cybersecurity, privacy, and operational risks.",
                "3. Risk Treatment and Contractual Security — Develop strategies to treat identified risks, including negotiating security-focused contractual agreements, defining clear roles and responsibilities, and ensuring that third parties meet security standards.",
                "4. Ongoing Monitoring and Auditing — Establish a system for continuous monitoring and regular audits of third-party activities to ensure compliance with security requirements and proactively identify new risks.",
                "5. Incident Response and Communication Protocols — Develop and implement incident response protocols to address security incidents involving third-party suppliers, ensuring clear communication and rapid response to minimize damage.",
                "6. Performance Reporting — Regularly report on third-party risks, incidents, and treatment effectiveness to ensure visibility and informed decision-making by senior management.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of Third-Party & Supplier Security Risk Management
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Improved Risk Mitigation",
                "Regulatory Compliance",
                "Business Continuity and Resilience",
                "Enhanced Supplier Relationships",
                "Cost Savings",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Implement */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Implement Third-Party & Supplier Security Risk Management
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              The process for implementing third-party and supplier security
              risk management typically involves the following steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Identify Third-Party Relationships — Identify and categorize all third-party relationships that involve access to sensitive data, IT infrastructure, or critical services.",
                "2. Perform Due Diligence and Risk Assessment — Conduct a thorough risk assessment to evaluate the security and resilience of each third-party supplier. Assess their compliance with regulatory requirements and industry standards.",
                "3. Develop Risk Treatment Strategies — Create risk treatment plans that outline specific actions for mitigating identified risks, including security controls, contractual agreements, and performance monitoring.",
                "4. Monitor and Review — Continuously monitor third-party activities, conduct security audits, and review performance against contractual agreements to ensure ongoing compliance with security requirements.",
                "5. Incident Response and Communication — Establish incident response protocols to address security incidents involving third-party suppliers, ensuring clear communication and rapid response to minimize damage.",
                "6. Continuous Improvement — Regularly review and update your third-party risk management processes, incorporating lessons learned from incidents, audits, and changing business needs.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for Third-Party & Supplier Security Risk
              Management?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. offers expert third-party and supplier
              security risk management consultancy services, helping businesses
              build robust risk management practices for securing external
              relationships and mitigating supplier risks.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Third-Party Risk Assessment and Due Diligence",
                "Supplier Security Evaluation and Monitoring",
                "Contractual Risk Management and SLA Negotiation",
                "Incident Response and Security Protocols",
                "Internal Audits and Compliance Support",
                "Continuous Risk Monitoring and Reporting",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses ensure third-party security, mitigate supplier
              risks, and enhance business continuity through proactive and
              effective risk management strategies.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ThirdPartySupplierSecurity_Details;
